(() => {
  const ROOT = '.aeya-recs';

  // ---- carousel arrows ----
  const trackFor = (btn) => {
    const root = btn.closest(ROOT);
    return root ? root.querySelector('[data-track]') : null;
  };

  const page = (track) => {
    const first = track.querySelector('.aeya-recs__item');
    if (!first) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
    return first.getBoundingClientRect().width + gap;
  };

  const updateState = (track) => {
    const root = track.closest(ROOT);
    if (!root) return;
    const prev = root.querySelector('[data-dir="prev"]');
    const next = root.querySelector('[data-dir="next"]');
    if (!prev || !next) return;
    const max = track.scrollWidth - track.clientWidth - 1;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= max;
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.aeya-recs__arrow');
    if (!btn) return;
    const track = trackFor(btn);
    if (!track) return;
    const dir = btn.dataset.dir === 'prev' ? -1 : 1;
    track.scrollBy({ left: dir * page(track), behavior: 'smooth' });
  });

  document.addEventListener(
    'scroll',
    (e) => {
      const track = e.target.closest && e.target.closest('[data-track]');
      if (track) updateState(track);
    },
    { capture: true, passive: true }
  );

  const syncAll = () => {
    document.querySelectorAll(`${ROOT} [data-track]`).forEach(updateState);
  };

  window.addEventListener('resize', syncAll);

  // ---- recommendations merge: complementary -> related -> fallback ----
  // The Product Recommendations API renders one intent per request, so we
  // fetch complementary and related separately and stitch them together in the
  // priority order the merchant asked for, backfilling from the server-rendered
  // fallback collection to fill any remaining slots.
  const itemsFrom = (html) => {
    if (!html || !html.trim()) return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const track = doc.querySelector('[data-track]');
    return track ? Array.from(track.querySelectorAll('.aeya-recs__item')) : [];
  };

  const loadRecs = async (root) => {
    if (root.dataset.recsLoaded) return;
    root.dataset.recsLoaded = '1';

    const { productId, sectionId, url, limit } = root.dataset;
    if (!productId || !sectionId || !url) return;
    const lim = parseInt(limit, 10) || 4;
    const endpoint = (intent) =>
      `${url}&product_id=${encodeURIComponent(productId)}&section_id=${encodeURIComponent(sectionId)}&intent=${intent}`;

    let complementary = [];
    let related = [];
    try {
      const [c, r] = await Promise.all([
        fetch(endpoint('complementary')).then((res) => (res.ok ? res.text() : '')),
        fetch(endpoint('related')).then((res) => (res.ok ? res.text() : '')),
      ]);
      complementary = itemsFrom(c);
      related = itemsFrom(r);
    } catch (e) {
      return; // network error: leave the server-rendered fallback in place
    }

    const track = root.querySelector('[data-track]');
    if (!track) return;
    const fallback = Array.from(track.querySelectorAll('.aeya-recs__item[data-fallback]'));

    const seen = new Set([String(productId)]);
    const merged = [];
    for (const node of [...complementary, ...related, ...fallback]) {
      const pid = node.getAttribute('data-pid');
      if (!pid || seen.has(pid)) continue;
      seen.add(pid);
      merged.push(node);
      if (merged.length >= lim) break;
    }

    if (!merged.length) return; // nothing to show: keep the skeleton / current state

    track.classList.remove('aeya-recs__track--skeleton');
    track.removeAttribute('aria-hidden');
    track.replaceChildren(...merged.map((node) => document.importNode(node, true)));

    const arrows = root.querySelector('.aeya-recs__arrows');
    if (arrows) arrows.hidden = merged.length <= 4;
    updateState(track);
  };

  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        loadRecs(entry.target);
      });
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  const init = () => {
    document.querySelectorAll(`${ROOT}[data-aeya-recs]`).forEach((root) => {
      if (root.dataset.recsInit) return;
      root.dataset.recsInit = '1';
      // Theme editor: the recommendations API returns nothing useful in the
      // preview, so keep the server-rendered fallback and skip fetching.
      if (window.Shopify && window.Shopify.designMode) return;
      io.observe(root);
    });
  };

  if (document.readyState !== 'loading') {
    init();
    syncAll();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      syncAll();
    });
  }

  document.addEventListener('shopify:section:load', () => {
    init();
    syncAll();
  });
})();
